/**
 * Platform-агностик API для универсальной работы
 *
 * Поддерживает:
 * - Telegram Mini App
 * - Max Messenger Mini App
 * - Standalone PWA
 * - Regular Web App
 *
 * Использование:
 * import { Platform } from './utils/platform';
 *
 * if (Platform.isTelegram()) {
 *   // Telegram-specific logic
 * } else if (Platform.isMax()) {
 *   // Max-specific logic
 * }
 */

export const Platform = {
  // ============================================
  // Определение платформы
  // ============================================

  /**
   * Проверка запуска в Max Messenger Mini App
   * Max Bridge SDK инжектит window.WebApp БЕЗ window.Telegram.WebApp
   * @returns {boolean}
   */
  isMax: () => {
    return !!window.WebApp && !window.Telegram?.WebApp;
  },

  /**
   * Проверка запуска в Telegram Mini App
   * Проверяем несколько признаков т.к. initData может быть пустым в dev режиме
   * @returns {boolean}
   */
  isTelegram: () => {
    // Исключаем Max (у Max тоже может быть window.Telegram-подобный объект)
    if (Platform.isMax()) return false;
    // Проверка 1: есть initData (надёжный признак production)
    if (window.Telegram?.WebApp?.initData && window.Telegram.WebApp.initData.length > 0) {
      return true;
    }
    // Проверка 2: WebApp объект имеет platform (любой кроме unknown = реальный Telegram)
    // Возможные значения: ios, android, android_x, macos, tdesktop, weba, webk, unknown
    const platform = window.Telegram?.WebApp?.platform;
    if (platform && platform !== 'unknown') {
      return true;
    }
    // Проверка 3: URL содержит tgWebAppData (открыто через Telegram)
    if (window.location.search.includes('tgWebApp') || window.location.hash.includes('tgWebApp')) {
      return true;
    }
    return false;
  },

  /**
   * Проверка запуска как standalone app (вне Telegram и Max)
   * @returns {boolean}
   */
  isStandalone: () => {
    return !Platform.isTelegram() && !Platform.isMax();
  },

  /**
   * Тип платформы
   * @returns {'telegram' | 'max' | 'web'}
   */
  getPlatformType: () => {
    if (Platform.isTelegram()) return 'telegram';
    if (Platform.isMax()) return 'max';
    return 'web';
  },

  /**
   * Проверка PWA режима (установленное приложение)
   * @returns {boolean}
   */
  isPWA: () => {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true
    );
  },

  /**
   * Определение мобильного устройства
   * @returns {boolean}
   */
  isMobile: () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  },

  /**
   * Определение iPhone
   * @returns {boolean}
   */
  isIPhone: () => {
    return /iPhone/.test(navigator.userAgent) && !window.MSStream;
  },

  /**
   * Определение iPad (включая iPadOS 13+ которые сообщают себя как Mac)
   * iPadOS 13+ идентифицирует себя как "Macintosh" но имеет touch support
   * @returns {boolean}
   */
  isIPad: () => {
    // Классический iPad (до iPadOS 13)
    if (/iPad/.test(navigator.userAgent)) {
      return true;
    }
    // iPadOS 13+ на M-чипах представляется как Mac, но имеет multi-touch
    // Условие: platform = MacIntel + более 1 touch point = iPad
    if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) {
      return true;
    }
    return false;
  },

  /**
   * Определение iOS (iPhone, iPad, iPod)
   * @returns {boolean}
   */
  isIOS: () => {
    return Platform.isIPhone() || Platform.isIPad() || /iPod/.test(navigator.userAgent);
  },

  /**
   * Определение Mac (настольный, не iPad)
   * @returns {boolean}
   */
  isMac: () => {
    // Mac - это MacIntel/MacPPC БЕЗ touch support (отличаем от iPad)
    return /Macintosh|MacIntel|MacPPC/.test(navigator.platform) &&
           navigator.maxTouchPoints === 0;
  },

  /**
   * Определение любого Apple устройства (iPhone, iPad, Mac)
   * Используется для применения iOS-style UI
   * @returns {boolean}
   */
  isApple: () => {
    return Platform.isIOS() || Platform.isMac();
  },

  /**
   * Определение Android
   * @returns {boolean}
   */
  isAndroid: () => {
    return /Android/i.test(navigator.userAgent);
  },

  // ============================================
  // User API (универсальный доступ к пользователю)
  // ============================================

  /**
   * Получение данных пользователя
   * @returns {Object} User data
   */
  getUser: () => {
    if (Platform.isTelegram()) {
      const user = window.Telegram.WebApp.initDataUnsafe.user;
      return {
        id: user?.id,
        firstName: user?.first_name,
        lastName: user?.last_name,
        username: user?.username,
        languageCode: user?.language_code,
        isPremium: user?.is_premium,
        photoUrl: user?.photo_url,
      };
    }

    if (Platform.isMax()) {
      const webapp = window.WebApp;
      const user = webapp?.initDataUnsafe?.user;
      if (!user) return null;
      // После auth/init используем внутренний user_id для API-вызовов,
      // т.к. Max user_id в БД хранится как отрицательный синтетический telegram_id
      const internalUserId = Platform.getInternalUserId();
      return {
        id: internalUserId || user.id,
        maxUserId: user.id,
        firstName: user.first_name,
        lastName: user.last_name,
        username: user.username,
        languageCode: user.language_code,
        isPremium: false,
        photoUrl: user.photo_url,
      };
    }

    // Standalone: проверяем localStorage
    try {
      const savedUser = localStorage.getItem('user');
      return savedUser ? JSON.parse(savedUser) : null;
    } catch (e) {
      console.error('Error parsing user from localStorage:', e);
      return null;
    }
  },

  /**
   * Сохранение пользователя (только standalone)
   * SECURITY NOTE: stores firstName/lastName in localStorage (needed for UI before profile loads).
   * Acceptable trade-off: names are low-sensitivity PII, and standalone mode needs them
   * for order creation and greeting display before the profile API responds.
   * @param {Object} user User data
   */
  saveUser: (user) => {
    if (Platform.isStandalone()) {
      try {
        // Store minimal data only (id + first name for UI, no sensitive PII)
        const minimalUser = {
          id: user.id,
          firstName: user.firstName || user.first_name,
          platform: user.platform || 'web',
        };
        localStorage.setItem('user', JSON.stringify(minimalUser));
      } catch (e) {
        // Ignore storage errors
      }
    }
  },

  /**
   * Сохранение внутреннего user_id после auth/init (для Max и других не-Telegram платформ)
   * @param {number} userId Internal DB user_id
   */
  saveInternalUserId: (userId) => {
    try {
      localStorage.setItem('internal_user_id', userId.toString());
    } catch (e) {
      console.error('Error saving internal user id:', e);
    }
  },

  /**
   * Получение внутреннего user_id (сохранённого после auth/init)
   * @returns {number|null}
   */
  getInternalUserId: () => {
    try {
      const id = localStorage.getItem('internal_user_id');
      return id ? parseInt(id, 10) : null;
    } catch (e) {
      return null;
    }
  },

  /**
   * Удаление пользователя (logout)
   */
  clearUser: () => {
    if (Platform.isStandalone()) {
      localStorage.removeItem('user');
    }
    localStorage.removeItem('internal_user_id');
  },

  /**
   * Проверка авторизации
   * @returns {boolean}
   */
  isAuthenticated: () => {
    if (Platform.isTelegram()) {
      return !!window.Telegram?.WebApp?.initDataUnsafe?.user;
    }
    if (Platform.isMax()) {
      return !!window.WebApp?.initDataUnsafe?.user;
    }
    return !!Platform.getUser();
  },

  // ============================================
  // UI Controls (универсальные элементы интерфейса)
  // ============================================

  /**
   * Показать главную кнопку (MainButton)
   * @param {string} text Текст кнопки
   * @param {Function} onClick Обработчик клика
   * @returns {Object|null} Button object для standalone или null
   */
  showMainButton: (text, onClick) => {
    if (Platform.isTelegram()) {
      const btn = window.Telegram.WebApp.MainButton;
      btn.setText(text);
      btn.onClick(onClick);
      btn.show();
      return null;
    } else {
      // Standalone: возвращаем объект для рендера нативной кнопки
      return {
        text,
        onClick,
        visible: true,
      };
    }
  },

  /**
   * Скрыть главную кнопку
   */
  hideMainButton: () => {
    if (Platform.isTelegram()) {
      window.Telegram.WebApp.MainButton.hide();
    }
  },

  /**
   * Обновить текст главной кнопки
   * @param {string} text Новый текст
   */
  updateMainButtonText: (text) => {
    if (Platform.isTelegram()) {
      window.Telegram.WebApp.MainButton.setText(text);
    }
  },

  /**
   * Показать кнопку "Назад"
   * @param {Function} onClick Обработчик клика
   */
  showBackButton: (onClick) => {
    if (Platform.isTelegram()) {
      try {
        const btn = window.Telegram.WebApp.BackButton;
        if (btn && btn.onClick && typeof btn.onClick === 'function') {
          btn.onClick(onClick);
          btn.show();
        }
      } catch (e) {
        // BackButton not supported in this Telegram version
      }
    }
    // Standalone: используем history.back() или custom handler
  },

  /**
   * Скрыть кнопку "Назад"
   */
  hideBackButton: () => {
    if (Platform.isTelegram()) {
      try {
        const btn = window.Telegram.WebApp.BackButton;
        if (btn && btn.hide && typeof btn.hide === 'function') {
          btn.hide();
        }
      } catch (e) {
        // BackButton not supported in this Telegram version
      }
    }
  },

  /**
   * Закрыть приложение
   */
  close: () => {
    if (Platform.isTelegram()) {
      window.Telegram.WebApp.close();
    } else if (Platform.isMax()) {
      window.WebApp.close();
    } else {
      // Standalone: можно показать confirmation
      if (confirm('Вы уверены, что хотите выйти?')) {
        window.close();
      }
    }
  },

  // ============================================
  // Notifications (универсальные уведомления)
  // ============================================

  /**
   * Показать алерт
   * @param {string} message Сообщение
   * @param {Function} callback Callback после закрытия
   */
  showAlert: (message, callback) => {
    if (Platform.isTelegram()) {
      window.Telegram.WebApp.showAlert(message, callback);
    } else {
      alert(message);
      if (callback) callback();
    }
  },

  /**
   * Показать confirm диалог
   * @param {string} message Сообщение
   * @param {Function} callback Callback с результатом (true/false)
   */
  showConfirm: (message, callback) => {
    if (Platform.isTelegram()) {
      window.Telegram.WebApp.showConfirm(message, callback);
    } else {
      const result = confirm(message);
      if (callback) callback(result);
    }
  },

  /**
   * Показать popup (Telegram 6.2+)
   * @param {Object} params Параметры popup
   * @param {Function} callback Callback с ID нажатой кнопки
   */
  showPopup: (params, callback) => {
    if (Platform.isTelegram() && window.Telegram.WebApp.showPopup) {
      window.Telegram.WebApp.showPopup(params, callback);
    } else {
      // Fallback на alert/confirm
      Platform.showAlert(params.message, callback);
    }
  },

  // ============================================
  // Haptic Feedback (тактильная отдача)
  // ============================================

  /**
   * Тактильная отдача при событии
   * @param {string} type Тип: 'light', 'medium', 'heavy', 'rigid', 'soft'
   */
  hapticFeedback: (type = 'medium') => {
    const hf = Platform.isTelegram()
      ? window.Telegram.WebApp.HapticFeedback
      : Platform.isMax()
        ? window.WebApp?.HapticFeedback
        : null;

    if (hf) {
      switch (type) {
        case 'light':
        case 'medium':
        case 'heavy':
        case 'rigid':
        case 'soft':
          hf.impactOccurred(type);
          break;
        case 'success':
          hf.notificationOccurred('success');
          break;
        case 'warning':
          hf.notificationOccurred('warning');
          break;
        case 'error':
          hf.notificationOccurred('error');
          break;
        default:
          hf.impactOccurred('medium');
      }
    }
    // Standalone: Web Vibration API
    else if ('vibrate' in navigator) {
      const patterns = {
        light: 10,
        medium: 20,
        heavy: 30,
        success: [10, 50, 10],
        warning: [20, 100],
        error: [50, 50, 50],
      };
      navigator.vibrate(patterns[type] || patterns.medium);
    }
  },

  // ============================================
  // Theme (тема оформления)
  // ============================================

  /**
   * Получить текущую тему
   * @returns {string} 'light' или 'dark'
   */
  getColorScheme: () => {
    if (Platform.isTelegram()) {
      return window.Telegram.WebApp.colorScheme || 'light';
    }
    if (Platform.isMax()) {
      return window.WebApp?.colorScheme || 'light';
    }
    // Standalone: проверяем system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  },

  /**
   * Получить цвета темы Telegram
   * @returns {Object} Theme colors
   */
  getThemeParams: () => {
    if (Platform.isTelegram()) {
      return window.Telegram.WebApp.themeParams || {};
    }
    // Standalone: дефолтные цвета
    return {
      bg_color: '#ffffff',
      text_color: '#000000',
      hint_color: '#999999',
      link_color: '#0EA5E9',
      button_color: '#0EA5E9',
      button_text_color: '#ffffff',
    };
  },

  /**
   * Установить цвет header (Telegram 6.1+)
   * @param {string} color HEX color
   */
  setHeaderColor: (color) => {
    if (Platform.isTelegram() && window.Telegram.WebApp.setHeaderColor) {
      window.Telegram.WebApp.setHeaderColor(color);
    }
  },

  /**
   * Установить цвет background (Telegram 6.1+)
   * @param {string} color HEX color
   */
  setBackgroundColor: (color) => {
    if (Platform.isTelegram() && window.Telegram.WebApp.setBackgroundColor) {
      window.Telegram.WebApp.setBackgroundColor(color);
    }
  },

  // ============================================
  // Navigation & Deep Links
  // ============================================

  /**
   * Открыть внешнюю ссылку
   * @param {string} url URL для открытия
   */
  openLink: (url) => {
    if (Platform.isTelegram()) {
      window.Telegram.WebApp.openLink(url);
    } else {
      window.open(url, '_blank');
    }
  },

  /**
   * Открыть Telegram ссылку (только в Telegram)
   * @param {string} url t.me ссылка
   */
  openTelegramLink: (url) => {
    if (Platform.isTelegram() && window.Telegram.WebApp.openTelegramLink) {
      window.Telegram.WebApp.openTelegramLink(url);
    } else if (Platform.isMax() && window.WebApp?.openLink) {
      window.WebApp.openLink(url);
    }
  },

  /**
   * Запросить номер телефона пользователя (Telegram 6.1+)
   * @param {Function} callback Callback с результатом (success, data)
   * @returns {boolean} true если запрос отправлен
   */
  requestContact: (callback) => {
    if (Platform.isTelegram() && window.Telegram.WebApp.requestContact) {
      window.Telegram.WebApp.requestContact((success, data) => {
        if (success && data) {
          // data содержит объект contact напрямую
          if (callback) callback(true, {
            phone_number: data.phone_number || data.phoneNumber,
            first_name: data.first_name || data.firstName,
            last_name: data.last_name || data.lastName,
            user_id: data.user_id || data.userId
          });
        } else {
          if (callback) callback(false, null);
        }
      });
      return true;
    }
    // Standalone: метод недоступен
    if (callback) callback(false, null);
    return false;
  },

  // ============================================
  // Data & Storage
  // ============================================

  /**
   * Сохранить данные в Cloud Storage (только Telegram)
   * @param {string} key Ключ
   * @param {string} value Значение
   * @param {Function} callback Callback после сохранения
   */
  cloudStorageSet: (key, value, callback) => {
    if (Platform.isTelegram() && window.Telegram.WebApp.CloudStorage) {
      window.Telegram.WebApp.CloudStorage.setItem(key, value, callback);
    } else {
      // Fallback на localStorage
      localStorage.setItem(`cloud_${key}`, value);
      if (callback) callback(null, true);
    }
  },

  /**
   * Получить данные из Cloud Storage (только Telegram)
   * @param {string} key Ключ
   * @param {Function} callback Callback с данными
   */
  cloudStorageGet: (key, callback) => {
    if (Platform.isTelegram() && window.Telegram.WebApp.CloudStorage) {
      window.Telegram.WebApp.CloudStorage.getItem(key, callback);
    } else {
      // Fallback на localStorage
      const value = localStorage.getItem(`cloud_${key}`);
      if (callback) callback(null, value);
    }
  },

  // ============================================
  // Utilities
  // ============================================

  /**
   * Готовность приложения (для Telegram и Max)
   */
  ready: () => {
    if (Platform.isTelegram()) {
      window.Telegram.WebApp.ready();
    } else if (Platform.isMax()) {
      window.WebApp.ready();
    }
  },

  /**
   * Expand приложение на весь экран (Telegram и Max)
   */
  expand: () => {
    if (Platform.isTelegram()) {
      window.Telegram.WebApp.expand();

      // Отключаем vertical swipes для нормального скролла
      if (window.Telegram.WebApp.disableVerticalSwipes) {
        window.Telegram.WebApp.disableVerticalSwipes();
      }
    } else if (Platform.isMax()) {
      if (window.WebApp.expand) window.WebApp.expand();
    }
  },

  /**
   * Инициализация платформы — вызывает ready() + expand()
   */
  init: () => {
    Platform.ready();
    Platform.expand();
  },

  /**
   * Версия Web App API
   * @returns {string}
   */
  getVersion: () => {
    if (Platform.isTelegram()) {
      return window.Telegram.WebApp.version || '6.0';
    }
    if (Platform.isMax()) {
      return window.WebApp?.version || '1.0';
    }
    return 'standalone';
  },

  /**
   * Получить init data (для отправки на backend)
   * @returns {string}
   */
  getInitData: () => {
    if (Platform.isTelegram()) {
      return window.Telegram.WebApp.initData || '';
    }
    if (Platform.isMax()) {
      return window.WebApp?.initData || '';
    }
    return '';
  },

  /**
   * Отправить данные боту (Telegram sendData / Max аналог)
   * @param {Object|string} data Данные для отправки
   */
  sendData: (data) => {
    const jsonData = typeof data === 'string' ? data : JSON.stringify(data);
    if (Platform.isTelegram() && window.Telegram?.WebApp?.sendData) {
      window.Telegram.WebApp.sendData(jsonData);
    } else if (Platform.isMax() && window.WebApp?.sendData) {
      window.WebApp.sendData(jsonData);
    }
  },

  // ============================================
  // JWT Auth Token (localStorage)
  // ============================================

  /**
   * Сохранить JWT auth token (in-memory + localStorage fallback)
   * @param {string} token JWT token
   * @param {string} refreshToken Optional refresh token
   */
  saveAuthToken: (token, refreshToken = null) => {
    // Primary: in-memory (XSS-safe)
    Platform._authToken = token;
    // Fallback: localStorage for page refresh persistence
    try {
      localStorage.setItem('auth_token', token);
      if (refreshToken) {
        localStorage.setItem('refresh_token', refreshToken);
      }
    } catch (e) {
      // Silently fail for environments without localStorage
    }
  },

  /**
   * Получить JWT auth token (in-memory first, then localStorage)
   * @returns {string|null}
   */
  getAuthToken: () => {
    // In-memory token takes priority (set during current session)
    if (Platform._authToken) return Platform._authToken;
    try {
      const stored = localStorage.getItem('auth_token');
      if (stored) Platform._authToken = stored;
      return stored;
    } catch (e) {
      return null;
    }
  },

  /**
   * Удалить JWT auth token
   */
  clearAuthToken: () => {
    Platform._authToken = null;
    try {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
    } catch (e) {
      // ignore
    }
  },

  // In-memory token (not accessible via XSS on localStorage)
  _authToken: null,

  /**
   * Логирование для отладки
   * @param {string} message Сообщение
   * @param {any} data Данные
   */
  log: (message, data = null) => {
    const platform = Platform.isTelegram() ? 'Telegram' : Platform.isMax() ? 'Max' : 'Standalone';
    console.log(`[Platform: ${platform}] ${message}`, data || '');
  },
};

// ============================================
// React Hook (опционально)
// ============================================

/**
 * React Hook для использования Platform API
 *
 * Usage:
 * const platform = usePlatform();
 *
 * if (platform.isTelegram) {
 *   // Telegram-specific code
 * }
 */
export const usePlatform = () => {
  const [state, setState] = React.useState({
    isTelegram: Platform.isTelegram(),
    isMax: Platform.isMax(),
    isStandalone: Platform.isStandalone(),
    platformType: Platform.getPlatformType(),
    isPWA: Platform.isPWA(),
    isMobile: Platform.isMobile(),
    colorScheme: Platform.getColorScheme(),
    user: Platform.getUser(),
    isAuthenticated: Platform.isAuthenticated(),
  });

  React.useEffect(() => {
    // Подписка на изменения темы
    const handleThemeChange = () => {
      setState(prev => ({
        ...prev,
        colorScheme: Platform.getColorScheme(),
      }));
    };

    if (Platform.isTelegram()) {
      window.Telegram.WebApp.onEvent('themeChanged', handleThemeChange);
    } else {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      mediaQuery.addEventListener('change', handleThemeChange);
    }

    return () => {
      if (Platform.isTelegram()) {
        window.Telegram.WebApp.offEvent('themeChanged', handleThemeChange);
      }
    };
  }, []);

  return {
    ...state,
    Platform, // Доступ ко всем методам Platform API
  };
};

// Экспорт по умолчанию
export default Platform;
